import React, { useEffect, useState } from 'react';
import './Styles/SignInPage.css'; // Import the CSS file
import FullScreenLoader from '../../../components/Styles/FullScreenLoader'; // Import the full-screen loader
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { authUserByIdAndPassword } from '../../../infrastructures/api/authApi'; // Import the authApi
import { ApiError } from '../../../infrastructures/api/ApiError';
import { trackAuthEvent, addGlobalContext } from '../../../utils/datadog';
import { TRANSLATIONS } from '../../../infrastructures/types/Language';
import type { Language, LanguageCode } from '../../../infrastructures/types/Language';

const ACURA_FORGOT_PASSWORD_URL = 'https://prestage.myaccount.acura.com/forgotPassword';
const HONDA_FORGOT_PASSWORD_URL = 'https://prestage.myaccount.honda.com/forgotPassword';


type Country = 'US' | 'CA' | 'MX';
const COUNTRIES: Country[] = ['US', 'CA', 'MX'];

type Brand = 'honda' | 'acura';
const BRANDS: Brand[] = ['honda', 'acura'];
type Theme = 'light' | 'dark';
const THEMES: Theme[] = ['light', 'dark'];

// Smaller viewport drops (browser toolbars collapsing, etc.) aren't a keyboard.
const KEYBOARD_HEIGHT_THRESHOLD_PX = 150;

const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

const App = () => {
    const [id, setId] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false); // Loading state
    const [passwordType, setPasswordType] = useState("password");
    const [isValid, setIsValid] = useState(false);
    const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
    const [showEmailError, setShowEmailError] = useState(false);

    const url = new URL(window.location.href);

    const countryParam = url.searchParams.get('cc') ?? 'US';
    const country: Country = COUNTRIES.includes(countryParam as Country) ? countryParam as Country : 'US';

    const brandParam = url.searchParams.get('brand') ?? 'acura';
    const brand: Brand = BRANDS.includes(brandParam as Brand) ? brandParam as Brand : 'acura';

    const themeParam = url.searchParams.get('theme');
    const defaultTheme = brand === 'acura' ? 'dark' : 'light';
    const theme: Theme = themeParam && THEMES.includes(themeParam as Theme) ? themeParam as Theme : defaultTheme;

    const langParam = url.searchParams.get('lc') ?? 'en';
    const languageCode: LanguageCode = (langParam in TRANSLATIONS ? langParam as LanguageCode : 'en');
    const language: Language = TRANSLATIONS[languageCode];

    const forgotPasswordUrl = new URL(brand === 'honda' ? HONDA_FORGOT_PASSWORD_URL : ACURA_FORGOT_PASSWORD_URL);
    forgotPasswordUrl.searchParams.set('cc', country);
    forgotPasswordUrl.searchParams.set('lc', language.code);
    forgotPasswordUrl.searchParams.set('theme', theme);
    forgotPasswordUrl.searchParams.set('app', 'ca');


    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
        e.preventDefault();
        setError(''); // Clear any previous error message
        setLoading(true); // Start loading

        const state = url.searchParams.get('state');

        // Resolve the API base. The {brand} placeholder lets prod target brand-split
        // hosts (https://api.app.{brand}.com/v2); non-prod hosts have no placeholder,
        // so the substitution is a no-op there.
        const apiUrl = (window.ENV_CONFIG?.API_URL ?? process.env.REACT_APP_API_URL ?? '').replace('{brand}', brand);

        // Track login attempt
        trackAuthEvent('login_attempt', {
            brand,
            country,
            language: language.code,
        });

        try {
            const data = await authUserByIdAndPassword(
                id,
                password,
                state,
                apiUrl,
                language,
            ); // Call the API

            trackAuthEvent('login_success', {
                brand,
                country,
                language: language.code,
            });

            window.location.href = data.redirectUrl
        } catch (error: unknown) {
            console.error('Error:', error);
            const rawMessage = error instanceof Error ? error.message : String(error);
            const isAllowedTranslationError = rawMessage === language.incorrectCreds || rawMessage === language.loginFailure;
            // A message CABES sent back is displayed as-is; anything else that
            // isn't a known translation falls back to the generic message so we
            // never surface an unvetted internal error.
            const serverMessage = error instanceof ApiError ? error.serverMessage : undefined;
            const userMessage = serverMessage ?? (isAllowedTranslationError ? rawMessage : language.loginFailure);

            trackAuthEvent('login_failure', {
                brand,
                country,
                language: language.code,
                rawErrorMessage: rawMessage,
                displayedMessage: userMessage,
                errorName: error instanceof Error ? error.name : typeof error,
                httpStatus: error instanceof ApiError ? error.status : undefined,
                serverMessage,
            });

            setError(userMessage);
        } finally {
            setLoading(false); // Stop loading
        }
    };

    const IsHiddenPassword = () =>{
        return (
            <div data-testid="signin-password-toggle" style={{alignSelf: 'flex-end', marginTop: 'auto', marginBottom: 'auto', marginRight: '5px', paddingTop: '5px'}}>
                {passwordType === "password" && (
                    <VisibilityOffIcon
                        onClick={() => setPasswordType("text")}
                    />
                )}
                {passwordType === "text" && (
                    <VisibilityIcon
                        onClick={() => setPasswordType("password")}
                    />
                )}
            </div>
        );
    }


    useEffect(() => {
        // Validate against the trimmed values so a whitespace-only entry can't
        // enable submit, but send what the user actually typed: a password may
        // legitimately have leading or trailing spaces.
        setIsValid(isValidEmail(id) && password.trim().length > 0);
    }, [id, password]);

    // Set global context for brand, country, and language on component mount
    useEffect(() => {
        addGlobalContext('brand', brand);
        addGlobalContext('country', country);
        addGlobalContext('language', language.code);
    }, [brand, country, language.code]);

    // Hide the logo only while an on-screen keyboard is actually taking up
    // space, which we detect as the visual viewport shrinking well below the
    // tallest height we've seen. Keying off focus instead hid the logo on
    // desktop (and in devtools' mobile emulation), where no keyboard appears.
    // Heights are scaled back up by the pinch-zoom factor so zooming in isn't
    // mistaken for a keyboard, and a width change (rotation) resets the
    // baseline since the new orientation has a different full height.
    useEffect(() => {
        const viewport = window.visualViewport;
        if (!viewport) {
            return;
        }
        let width = viewport.width * viewport.scale;
        let fullHeight = viewport.height * viewport.scale;
        const handleResize = () => {
            const currentWidth = viewport.width * viewport.scale;
            const currentHeight = viewport.height * viewport.scale;
            if (currentWidth !== width) {
                width = currentWidth;
                fullHeight = currentHeight;
            }
            fullHeight = Math.max(fullHeight, currentHeight);
            setIsKeyboardOpen(fullHeight - currentHeight > KEYBOARD_HEIGHT_THRESHOLD_PX);
        };
        viewport.addEventListener('resize', handleResize);
        return () => viewport.removeEventListener('resize', handleResize);
    }, []);

    // Only flag the email once the user has typed something and moved on, so
    // they aren't told it's wrong while they're still typing it. Once shown,
    // the error clears as soon as the address becomes valid (or is emptied),
    // but it only reappears on the next blur.
    const handleEmailBlur = () => {
        setShowEmailError(id.trim().length > 0 && !isValidEmail(id));
    };

    const handleEmailChange = (value: string) => {
        setId(value);
        if (value.trim().length === 0 || isValidEmail(value)) {
            setShowEmailError(false);
        }
    };

    const Logo = () => {
        if (isKeyboardOpen) {
            return null;
        }
        if (brand === 'honda') {
            return <img className="logo logo-honda spin" src='/honda.svg' alt="Honda"/>;
        } else {
            return <img className="logo logo-acura spin" src='/acura.svg' alt="Acura"/>;
        }
    };

    return (
        <div data-testid="signin-page" className={`container background-${brand}`}>
            {loading && <FullScreenLoader />}
                <Logo />
            <div style={{marginTop: 'auto', justifyContent: 'left'}}>
                <h1 data-testid="signin-heading" className={`h1-${brand}`}>{language.signIn}</h1>
                <div className="error-message-row">
                    <img
                        src={brand === 'honda' ? '/honda_warning.svg' : '/acura_warning.svg'}
                        alt=""
                        className={`error-message-icon ${error ? '' : 'error-message-hidden'}`}
                    />
                    <p data-testid="signin-error" className={`error-message error-message-${brand} ${error ? '' : 'error-message-hidden'}`}>{error || '\u00A0'}</p>
                </div>
                <form
                 data-testid="signin-form"
                 style={{display: 'flex', flexDirection: 'column'}}
                 onSubmit={handleSubmit}
                 noValidate={true}
                 >
                    <label className={`label-${brand}`} htmlFor="email" style={{marginLeft: '8px'}}>{language.email}</label>
                    <div data-testid="signin-email-field" className={`form-input form-input-${brand} ${showEmailError ? `form-input-error-${brand}` : ''}`}>
                        {showEmailError && (
                            <img
                                data-testid="signin-email-error-icon"
                                src={brand === 'honda' ? '/honda_warning.svg' : '/acura_warning.svg'}
                                alt=""
                                className="field-error-icon"
                            />
                        )}
                        <input
                            id="email"
                            data-testid="signin-email-input"
                            className={`input-${brand}`}
                            type="email"
                            value={id}
                            onChange={
                                (e) => {
                                    handleEmailChange(e.target.value);
                                }
                            }
                            onBlur={handleEmailBlur}
                            aria-invalid={showEmailError}
                            aria-describedby={showEmailError ? 'email-error' : undefined}
                        />
                        <div data-testid="signin-email-clear" style={{alignSelf: 'flex-end', marginTop: 'auto', marginBottom: 'auto', marginRight: '5px', paddingTop: '5px', visibility: (id.length > 0 ? 'visible' : 'hidden')}}>
                            <HighlightOffIcon
                            onClick={() => {
                                handleEmailChange('');
                            }}
                            />
                        </div>
                    </div>
                    <p id="email-error" data-testid="signin-email-error" className={`field-error-message error-message-${brand} ${showEmailError ? '' : 'error-message-hidden'}`}>
                        {showEmailError ? language.invalidEmail : '\u00A0'}
                    </p>

                    <label className={`label-${brand}`} htmlFor="password" style={{marginTop: '16px', marginLeft: '8px'}}>{language.password}</label>
                    <div className={`form-input form-input-${brand}`}>
                        <input
                            id="password"
                            data-testid="signin-password-input"
                            className={`input-${brand}`}
                            type={passwordType}
                            value={password}
                            onChange={
                                (e) => {
                                    setPassword(e.target.value);
                                }
                            }
                        />
                        <IsHiddenPassword />
                    </div>
                    <a
                        data-testid="signin-forgot-password-link"
                        className={`forgot-password forgot-password-${brand}`}
                        href={forgotPasswordUrl.toString()}
                        onClick={() => {
                            trackAuthEvent('forgot_password_click', {
                                brand,
                                country,
                                language: language.code,
                            });
                        }}
                    >
                        {language.forgotPassword}
                    </a>
                    <button data-testid="signin-submit-button" className={`button-${brand}`} style={{width: '100%', alignSelf: 'center'}} type="submit" disabled={!isValid}>{language.signIn}</button>
                </form>

            </div>
        </div>
    );
};

export default App;
